import {
  Alert,
  Button,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import type { AutomationType } from '@playbook-technologies/common-enums';
import { Fragment, useState } from 'react';

import { AUTOMATION_COPY, AUTOMATION_ORDER } from '../../automation-copy';
import {
  LogRow,
  PER_PAGE_OPTIONS,
  useAutomationLogsViewModel,
} from '../../view-model/AutomationInsights.viewModel';
import * as styles from '../Automations.view.css';

const COLUMN_COUNT = 6;

const toAutomationTypeFilter = (value: string): AutomationType | '' => {
  const match = AUTOMATION_ORDER.find((type) => type === value);
  return match ?? '';
};

const StepDetail = ({ row }: { row: LogRow }) => (
  <div className={styles.stepDetail}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Step</TableCell>
          <TableCell>Sent</TableCell>
          <TableCell>Opened</TableCell>
          <TableCell>Clicked</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {row.steps.length === 0 ? (
          <TableRow>
            <TableCell colSpan={4}>No emails sent yet</TableCell>
          </TableRow>
        ) : (
          row.steps.map((step) => (
            <TableRow key={step.stepNo}>
              <TableCell>{step.stepNo}</TableCell>
              <TableCell>{step.sentAt}</TableCell>
              <TableCell>{step.openedAt}</TableCell>
              <TableCell>{step.clickedAt}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </div>
);

export const AutomationLogsView = ({ creatorId }: { creatorId: number }) => {
  const vm = useAutomationLogsViewModel(creatorId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (vm.type !== 'fetched') {
    return <Skeleton variant="rounded" height={320} />;
  }

  const { value } = vm;
  return (
    <div className={styles.adminToolsCard} data-testid="enrolled-people">
      <span className={styles.detailsSectionTitle}>Enrolled people</span>
      <div className={styles.filters}>
        <TextField
          size="small"
          label="Email (exact)"
          value={value.emailInput}
          error={Boolean(value.emailError)}
          helperText={value.emailError}
          onChange={(event) => value.setEmailInput(event.target.value)}
          onBlur={value.applyEmailFilter}
          onKeyDown={(event) => {
            if (event.key === 'Enter') value.applyEmailFilter();
          }}
          InputProps={{
            endAdornment: value.emailInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear email filter"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={value.clearEmailFilter}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <TextField
          size="small"
          select
          label="Automation"
          value={value.automationType}
          onChange={(event) =>
            value.setAutomationType(toAutomationTypeFilter(event.target.value))
          }
          className={styles.automationSelect}
        >
          <MenuItem value="">All automations</MenuItem>
          {AUTOMATION_ORDER.map((automationType) => (
            <MenuItem key={automationType} value={automationType}>
              {AUTOMATION_COPY[automationType].label}
            </MenuItem>
          ))}
        </TextField>
      </div>
      {value.loadError ? (
        <Alert
          severity="error"
          action={
            <Button onClick={() => void value.loadError?.retry()}>
              Try again
            </Button>
          }
        >
          {value.loadError.message}
        </Alert>
      ) : (
        <TableContainer className={value.isRefreshing ? styles.refreshing : ''}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Email</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>First sent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {value.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT}>
                    No enrolled people match these filters.
                  </TableCell>
                </TableRow>
              ) : (
                value.rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          aria-label={
                            expandedId === row.id
                              ? 'Hide step engagement'
                              : 'Show step engagement'
                          }
                          onClick={() =>
                            setExpandedId(expandedId === row.id ? null : row.id)
                          }
                        >
                          {expandedId === row.id ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.trigger}</TableCell>
                      <TableCell>{row.progress}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status.label}
                          color={
                            row.status.kind === 'goal'
                              ? 'success'
                              : row.status.kind === 'active'
                                ? 'primary'
                                : 'default'
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{row.firstSentAt}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={COLUMN_COUNT}
                        sx={{ padding: 0, border: 0 }}
                      >
                        <Collapse
                          in={expandedId === row.id}
                          unmountOnExit
                          timeout="auto"
                        >
                          <StepDetail row={row} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        count={value.total}
        page={value.page}
        onPageChange={(_event, page) => value.setPage(page)}
        rowsPerPage={value.perPage}
        rowsPerPageOptions={[...PER_PAGE_OPTIONS]}
        onRowsPerPageChange={(event) =>
          value.setPerPage(Number(event.target.value))
        }
      />
    </div>
  );
};
